import { toLatin } from '@elena/shared';
import type * as Sdk from 'microsoft-cognitiveservices-speech-sdk';
import { api, type SttEngine } from './api';
import { SAMPLE_RATE } from './vad';

/**
 * One listening session with a cloud recognizer, fed raw 16 kHz audio from the mic tap.
 * Audio pushed before the connection is up waits in memory, so nothing said is lost while the
 * key and the socket come up.
 *
 *   soniox      wss://stt-rt.soniox.com/transcribe-websocket (stt-rt-v5): understands Serbian
 *               and English in the same sentence; language hints sr + en, our words as context
 *   elevenlabs  wss://api.elevenlabs.io/v1/speech-to-text/realtime (Scribe v2 Realtime):
 *               language detected per sentence, our words as keyterms
 *   azure       the Speech SDK with a push stream: Serbian (sr-RS) with the phrase list
 */

export type CloudEngine = Exclude<SttEngine, 'browser'>;

export interface SessionOptions {
  /** BCP-47 language of the conversation, e.g. sr-RS. */
  lang: string;
  /** Words to expect: players, rooms, learned names... */
  phrases: string[];
  onPartial?: (text: string) => void;
  /** Azure only: the token and region from /api/stt. */
  azure?: { token: string; region: string } | null;
}

export interface SttSession {
  readonly engine: CloudEngine;
  push(pcm: Int16Array): void;
  /** Ends the audio and resolves with the guesses for everything said, best first. */
  finish(): Promise<string[]>;
  cancel(): void;
  /** Seconds of audio sent so far. */
  readonly seconds: number;
  /** What the recognizer answered, short (Elena's phone logs it). */
  readonly debug?: string;
}

export class SttFailure extends Error {
  constructor(
    message: string,
    /** The account is out of credit / quota. */
    readonly quota = false,
  ) {
    super(message);
  }
}

const latinFor = (lang: string) => (text: string) => (lang.startsWith('sr') ? toLatin(text) : text);

/** sr-RS → ["sr", "en"]: Serbian with English titles is the usual mix. */
const hintsFor = (lang: string): string[] => {
  const main = lang.slice(0, 2).toLowerCase();
  return main === 'en' ? ['en', 'sr'] : [main, 'en'];
};

const tidy = (text: string) => text.replace(/\s+/g, ' ').trim();

// ---------------------------------------------------------------------------------------------
// keys: Soniox and Scribe keys are single use; one spare is kept so a session never waits

const spare: Partial<Record<'soniox' | 'elevenlabs', { key: string; model: string; at: number }>> =
  {};
const SPARE_MAX_AGE_MS = { soniox: 4 * 60_000, elevenlabs: 12 * 60_000 };

async function takeKey(engine: 'soniox' | 'elevenlabs'): Promise<{ key: string; model: string }> {
  const kept = spare[engine];
  delete spare[engine];
  const fresh = kept && Date.now() - kept.at < SPARE_MAX_AGE_MS[engine] ? kept : null;
  // the next session's key, fetched in the background
  void refillKey(engine);
  if (fresh) return fresh;
  return keyFromServer(engine);
}

async function keyFromServer(engine: 'soniox' | 'elevenlabs') {
  try {
    const c = await api.sttKey(engine);
    return { key: c.key, model: c.model };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new SttFailure(message, /kredit|credit|quota/i.test(message));
  }
}

let refilling: Partial<Record<'soniox' | 'elevenlabs', Promise<void>>> = {};
export function refillKey(engine: 'soniox' | 'elevenlabs'): Promise<void> {
  if (spare[engine] && Date.now() - spare[engine]!.at < SPARE_MAX_AGE_MS[engine])
    return Promise.resolve();
  refilling[engine] ??= keyFromServer(engine)
    .then((k) => {
      spare[engine] = { ...k, at: Date.now() };
    })
    .catch(() => undefined)
    .finally(() => {
      refilling = { ...refilling, [engine]: undefined };
    });
  return refilling[engine]!;
}

// ---------------------------------------------------------------------------------------------
// a WebSocket session with a queue for audio pushed before it opened

abstract class SocketSession implements SttSession {
  abstract readonly engine: CloudEngine;
  protected ws: WebSocket | null = null;
  private queue: Int16Array[] = [];
  private sent = 0;
  protected closed = false;
  protected failure: SttFailure | null = null;
  protected done: Promise<string[]>;
  protected resolveDone!: (guesses: string[]) => void;
  protected rejectDone!: (err: SttFailure) => void;
  protected readonly latin: (text: string) => string;

  constructor(protected readonly options: SessionOptions) {
    this.latin = latinFor(options.lang);
    this.done = new Promise<string[]>((resolve, reject) => {
      this.resolveDone = resolve;
      this.rejectDone = reject;
    });
    // finish() may never be called (cancel); an unhandled rejection would be noise
    this.done.catch(() => undefined);
  }

  get seconds(): number {
    return this.sent / SAMPLE_RATE;
  }

  protected abstract open(): Promise<WebSocket>;
  protected abstract sendAudio(ws: WebSocket, pcm: Int16Array): void;
  protected abstract sendEnd(ws: WebSocket): void;

  start(): this {
    this.open()
      .then((ws) => {
        if (this.closed) {
          ws.close();
          return;
        }
        this.ws = ws;
        ws.addEventListener('close', () => {
          if (!this.closed) this.fail(new SttFailure('Veza sa prepoznavanjem je prekinuta.'));
        });
        for (const pcm of this.queue) this.sendAudio(ws, pcm);
        this.queue = [];
        if (this.ending) this.sendEnd(ws);
      })
      .catch((err: unknown) =>
        this.fail(err instanceof SttFailure ? err : new SttFailure(String(err))),
      );
    return this;
  }

  push(pcm: Int16Array): void {
    if (this.closed || this.ending) return;
    this.sent += pcm.length;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.sendAudio(this.ws, pcm);
    else this.queue.push(pcm);
  }

  private ending = false;
  finish(): Promise<string[]> {
    if (!this.ending && !this.closed) {
      this.ending = true;
      if (this.ws && this.ws.readyState === WebSocket.OPEN) this.sendEnd(this.ws);
      // the last words take a moment; never wait forever
      window.setTimeout(() => this.fail(new SttFailure('Prepoznavanje nije odgovorilo.')), 8000);
    }
    return this.done;
  }

  cancel(): void {
    this.closed = true;
    this.queue = [];
    try {
      this.ws?.close();
    } catch {
      // already closed
    }
    this.rejectDone(new SttFailure('canceled'));
  }

  protected succeed(guesses: string[]): void {
    if (this.closed) return;
    this.closed = true;
    this.resolveDone(guesses.map((g) => tidy(this.latin(g))).filter(Boolean));
    try {
      this.ws?.close();
    } catch {
      // already closed
    }
  }

  protected fail(err: SttFailure): void {
    if (this.closed) return;
    this.closed = true;
    this.failure = err;
    this.rejectDone(err);
    try {
      this.ws?.close();
    } catch {
      // already closed
    }
  }
}

/** Opens a socket and resolves once it is open (or rejects when it can't). */
function connect(url: string, timeoutMs = 8000): Promise<WebSocket> {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    const timer = window.setTimeout(() => {
      ws.close();
      reject(new SttFailure('Prepoznavanje se ne javlja.'));
    }, timeoutMs);
    ws.addEventListener('open', () => {
      window.clearTimeout(timer);
      resolve(ws);
    });
    ws.addEventListener('error', () => {
      window.clearTimeout(timer);
      reject(new SttFailure('Ne mogu da se povežem sa prepoznavanjem govora.'));
    });
  });
}

// ---------------------------------------------------------------------------------------------
// Soniox

interface SonioxToken {
  text: string;
  is_final: boolean;
}
interface SonioxResponse {
  tokens?: SonioxToken[];
  finished?: boolean;
  error_code?: number;
  error_message?: string;
}

class SonioxSession extends SocketSession {
  readonly engine = 'soniox' as const;
  private finalText = '';
  private messages = 0;
  debug = '';

  protected async open(): Promise<WebSocket> {
    const { key, model } = await takeKey('soniox');
    const ws = await connect('wss://stt-rt.soniox.com/transcribe-websocket');
    ws.send(
      JSON.stringify({
        api_key: key,
        model,
        audio_format: 'pcm_s16le',
        sample_rate: SAMPLE_RATE,
        num_channels: 1,
        language_hints: hintsFor(this.options.lang),
        context: {
          general: [
            { key: 'domain', value: 'Glasovna asistentkinja Elena' },
            {
              key: 'topic',
              value:
                'Komande na srpskom; nazivi pesama, izvođača, igrica i aplikacija često na engleskom',
            },
          ],
          terms: this.options.phrases.slice(0, 300),
        },
      }),
    );
    ws.addEventListener('message', (e: MessageEvent) => this.onMessage(e.data));
    return ws;
  }

  private onMessage(data: unknown): void {
    if (typeof data !== 'string') return;
    this.debug = `${++this.messages} poruka, sent ${Math.round(this.seconds * 10) / 10}s, poslednja: ${data.slice(0, 160)}`;
    let r: SonioxResponse;
    try {
      r = JSON.parse(data) as SonioxResponse;
    } catch {
      return;
    }
    if (r.error_code != null || r.error_message) {
      const quota = r.error_code === 402 || /credit|balance|quota/i.test(r.error_message ?? '');
      this.fail(new SttFailure(`Soniox: ${r.error_message ?? r.error_code}`, quota));
      return;
    }
    let pending = '';
    for (const t of r.tokens ?? []) {
      // <end> / <fin> markers are not words
      if (/^<[a-z]+>$/.test(t.text)) continue;
      if (t.is_final) this.finalText += t.text;
      else pending += t.text;
    }
    this.options.onPartial?.(tidy(this.latin(this.finalText + pending)));
    if (r.finished) this.succeed([this.finalText]);
  }

  protected sendAudio(ws: WebSocket, pcm: Int16Array): void {
    ws.send(pcm.slice().buffer);
  }

  protected sendEnd(ws: WebSocket): void {
    // an empty message = end of audio; the server sends the last words and finished: true
    ws.send('');
  }
}

// ---------------------------------------------------------------------------------------------
// ElevenLabs Scribe Realtime

function toBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

class ScribeSession extends SocketSession {
  readonly engine = 'elevenlabs' as const;
  private committed: string[] = [];

  protected async open(): Promise<WebSocket> {
    const { key, model } = await takeKey('elevenlabs');
    const q = new URLSearchParams({
      model_id: model,
      token: key,
      audio_format: 'pcm_16000',
      commit_strategy: 'manual',
    });
    // keyterms: at most 50, short ones
    for (const term of this.options.phrases.filter((p) => p.length <= 20).slice(0, 50)) {
      q.append('keyterms', term);
    }
    const ws = await connect(`wss://api.elevenlabs.io/v1/speech-to-text/realtime?${q.toString()}`);
    ws.addEventListener('message', (e: MessageEvent) => this.onMessage(e.data));
    return ws;
  }

  private onMessage(data: unknown): void {
    if (typeof data !== 'string') return;
    let m: { message_type?: string; text?: string; error?: string; message?: string };
    try {
      m = JSON.parse(data) as typeof m;
    } catch {
      return;
    }
    const type = m.message_type ?? '';
    if (type === 'partial_transcript') {
      this.options.onPartial?.(tidy(this.latin([...this.committed, m.text ?? ''].join(' '))));
    } else if (type === 'committed_transcript') {
      this.committed.push(m.text ?? '');
      this.options.onPartial?.(tidy(this.latin(this.committed.join(' '))));
      if (this.endSent) this.succeed([this.committed.join(' ')]);
    } else if (type === 'error' || type.endsWith('_error') || type.includes('quota')) {
      const detail = m.error ?? m.message ?? type;
      this.fail(new SttFailure(`ElevenLabs: ${detail}`, /quota|credit/i.test(`${type} ${detail}`)));
    }
  }

  protected sendAudio(ws: WebSocket, pcm: Int16Array): void {
    ws.send(
      JSON.stringify({
        message_type: 'input_audio_chunk',
        audio_base_64: toBase64(pcm),
        commit: false,
        sample_rate: SAMPLE_RATE,
      }),
    );
  }

  private endSent = false;
  protected sendEnd(ws: WebSocket): void {
    this.endSent = true;
    // commit = everything sent so far is one finished segment
    ws.send(
      JSON.stringify({
        message_type: 'input_audio_chunk',
        audio_base_64: '',
        commit: true,
        sample_rate: SAMPLE_RATE,
      }),
    );
  }
}

// ---------------------------------------------------------------------------------------------
// Azure (Speech SDK with a push stream)

let sdkLoad: Promise<typeof Sdk> | null = null;
export const loadAzureSdk = () => (sdkLoad ??= import('microsoft-cognitiveservices-speech-sdk'));

class AzureSession implements SttSession {
  readonly engine = 'azure' as const;
  private sent = 0;
  private queue: Int16Array[] = [];
  private stream: Sdk.PushAudioInputStream | null = null;
  private rec: Sdk.SpeechRecognizer | null = null;
  private parts: string[][] = [];
  private interim = '';
  private closed = false;
  private ending = false;
  private readonly done: Promise<string[]>;
  private resolveDone!: (g: string[]) => void;
  private rejectDone!: (e: SttFailure) => void;
  private readonly latin: (t: string) => string;

  constructor(private readonly options: SessionOptions) {
    this.latin = latinFor(options.lang);
    this.done = new Promise((resolve, reject) => {
      this.resolveDone = resolve;
      this.rejectDone = reject;
    });
    this.done.catch(() => undefined);
  }

  get seconds(): number {
    return this.sent / SAMPLE_RATE;
  }

  start(): this {
    const azure = this.options.azure;
    if (!azure) {
      this.fail(new SttFailure('Azure nije dostupan.'));
      return this;
    }
    loadAzureSdk()
      .then((sdk) => {
        if (this.closed) return;
        const config = sdk.SpeechConfig.fromAuthorizationToken(azure.token, azure.region);
        config.speechRecognitionLanguage = this.options.lang;
        config.outputFormat = sdk.OutputFormat.Detailed;
        const stream = sdk.AudioInputStream.createPushStream(
          sdk.AudioStreamFormat.getWaveFormatPCM(SAMPLE_RATE, 16, 1),
        );
        const rec = new sdk.SpeechRecognizer(config, sdk.AudioConfig.fromStreamInput(stream));
        if (this.options.phrases.length)
          sdk.PhraseListGrammar.fromRecognizer(rec).addPhrases(this.options.phrases);
        rec.recognizing = (_s, e) => {
          this.interim = e.result.text;
          this.partial();
        };
        rec.recognized = (_s, e) => {
          if (e.result.reason !== sdk.ResultReason.RecognizedSpeech || !e.result.text) return;
          let guesses = [e.result.text];
          try {
            const detail = JSON.parse(e.result.json) as { NBest?: Array<{ Display?: string }> };
            const nbest = (detail.NBest ?? []).map((n) => n.Display ?? '').filter(Boolean);
            if (nbest.length) guesses = nbest.slice(0, 3);
          } catch {
            // keep the plain text
          }
          this.parts.push(guesses);
          this.interim = '';
          this.partial();
        };
        rec.canceled = (_s, e) => {
          if (e.reason !== sdk.CancellationReason.Error) return;
          const quota =
            /quota/i.test(e.errorDetails) || e.errorCode === sdk.CancellationErrorCode.Forbidden;
          this.fail(new SttFailure(`Azure: ${e.errorDetails}`, quota));
        };
        rec.sessionStopped = () => this.succeed();
        this.stream = stream;
        this.rec = rec;
        for (const pcm of this.queue) stream.write(pcm.slice().buffer);
        this.queue = [];
        rec.startContinuousRecognitionAsync(undefined, (err) =>
          this.fail(new SttFailure(`Azure: ${err}`)),
        );
        if (this.ending) stream.close();
      })
      .catch(() => this.fail(new SttFailure('Azure SDK se nije učitao.')));
    return this;
  }

  private partial(): void {
    const words = [...this.parts.map((p) => p[0] ?? ''), this.interim].join(' ');
    this.options.onPartial?.(tidy(this.latin(words)));
  }

  push(pcm: Int16Array): void {
    if (this.closed || this.ending) return;
    this.sent += pcm.length;
    if (this.stream) this.stream.write(pcm.slice().buffer);
    else this.queue.push(pcm);
  }

  finish(): Promise<string[]> {
    if (!this.ending && !this.closed) {
      this.ending = true;
      // closing the stream lets Azure finish the last sentence, then the session stops
      this.stream?.close();
      window.setTimeout(() => this.succeed(), 6000);
    }
    return this.done;
  }

  cancel(): void {
    if (this.closed) return;
    this.closed = true;
    this.rejectDone(new SttFailure('canceled'));
    this.close();
  }

  /** Every combination of the phrases' guesses, the best first (as the browser path did). */
  private guesses(): string[] {
    const most = Math.max(1, ...this.parts.map((p) => p.length));
    const out: string[] = [];
    for (let k = 0; k < most; k++) {
      const whole = tidy(
        this.latin([...this.parts.map((p) => p[k] ?? p[0] ?? ''), this.interim].join(' ')),
      );
      if (whole && !out.includes(whole)) out.push(whole);
    }
    return out;
  }

  private succeed(): void {
    if (this.closed) return;
    this.closed = true;
    this.resolveDone(this.guesses());
    this.close();
  }

  private fail(err: SttFailure): void {
    if (this.closed) return;
    this.closed = true;
    this.rejectDone(err);
    this.close();
  }

  private close(): void {
    const rec = this.rec;
    this.rec = null;
    if (!rec) return;
    rec.stopContinuousRecognitionAsync(
      () => rec.close(),
      () => rec.close(),
    );
  }
}

/** Starts a session; audio can be pushed right away. */
export function openSession(engine: CloudEngine, options: SessionOptions): SttSession {
  if (engine === 'soniox') return new SonioxSession(options).start();
  if (engine === 'elevenlabs') return new ScribeSession(options).start();
  return new AzureSession(options).start();
}
